import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { animate, duration, easing } from "@/shared/lib";
import { tactile } from "@/shared/telegram";

/**
 * Figma: `AmountInput/Trading` (18:47).
 *
 * Везде, где сумму вводит пользователь: пополнение и вывод с баланса, запуск
 * робота и пополнение его капитала. До перехода на минимальную сумму цена
 * робота была фиксированной, и в покупке блок стоял только на чтение — теперь
 * сумму выбирают, и пресеты вернулись рядом (DESIGN.md §8).
 *
 * Значение хранится строкой и строкой же уходит в API: деньги в контракте —
 * строки, а `Number("100.10")` уже теряет копейки. Никакой арифметики над
 * введённым значением клиент не делает (правило ТЗ №5).
 *
 * Движение (FE-13): отказ сервера встряхивает поле и отзывается тактильно.
 * Ошибка приходит после нажатия на CTA, то есть внимание пользователя в этот
 * момент внизу экрана, — без движения он ищет причину глазами.
 */
interface AmountInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Подпись под полем: минимум, доступный остаток и т.п. */
  hint?: string;
  /** Текст ошибки от бэкенда — показывается вместо подсказки, у поля. */
  error?: string | undefined;
  suffix?: string;
}

export function AmountInput({ value, onChange, hint, error, suffix = "USDT" }: AmountInputProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLLabelElement>(null);

  useEffect(() => {
    if (!error) return;
    const element = ref.current;
    if (!element) return;

    animate(
      element,
      [
        { transform: "translate3d(0, 0, 0)" },
        { transform: "translate3d(-6px, 0, 0)" },
        { transform: "translate3d(5px, 0, 0)" },
        { transform: "translate3d(-3px, 0, 0)" },
        { transform: "translate3d(0, 0, 0)" },
      ],
      { duration: duration("slow"), easing: easing("standard") },
    );
    tactile("error");
  }, [error]);

  return (
    <div className="flex flex-col gap-2">
      <label
        ref={ref}
        className={[
          "flex items-center justify-between rounded-2xl border bg-background-elevated px-4 py-[18px]",
          "transition-colors duration-[var(--motion-fast)] ease-standard",
          error ? "border-border-loss" : "border-border-subtle",
        ].join(" ")}
      >
        <span className="sr-only">{t("wallet.amountLabel")}</span>
        <input
          value={value}
          onChange={(event) => {
            // Пропускаем только цифры и одну точку: буквы и минус бэкенд всё
            // равно отвергнет 422, но пользователь узнает об этом позже.
            const next = event.target.value.replace(",", ".");
            if (next === "" || /^\d*\.?\d{0,2}$/.test(next)) onChange(next);
          }}
          inputMode="decimal"
          placeholder="0.00"
          aria-invalid={error ? true : undefined}
          className="w-full bg-transparent text-h2 font-bold text-text-primary outline-none placeholder:text-text-tertiary"
        />
        <span className="shrink-0 pl-3 text-label font-semibold text-text-secondary">{suffix}</span>
      </label>

      {error ? (
        <span role="alert" className="animate-rise-in text-caption font-medium text-status-loss">
          {error}
        </span>
      ) : hint ? (
        <span className="text-caption font-normal text-text-secondary">{hint}</span>
      ) : null}
    </div>
  );
}
